import {CSSProperties, useCallback, useEffect, useMemo, useRef} from "react";
import {getTrackBackground, Range as ReactRange} from "react-range";
import {IRenderTrackParams} from "react-range/lib/types";
import classNames from "classnames";

import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {isNumber} from "@pg-mono/nodash";

import {useValuesWithTransformation} from "../hooks/use_values_with_transformation";
import {IRange} from "../types/IRange";
import {RangeColorVariant} from "../types/RangeColorVariant";
import {RangeSize} from "../types/RangeSize";
import {RangeType} from "../types/RangeType";
import {UnitsType} from "../types/UnitsType";
import {getLabelFromExtremum} from "../utils/get_label_from_extremum";

import * as cns from "./Range.module.css";

interface IProps {
    name: string;
    value: IRange<RangeType>;
    error?: string[];
    onChange: (fieldName: string, value: IRange<RangeType>) => void;
    step: number;
    min: number;
    max: number;
    unitType?: UnitsType;
    useDynamicChange?: boolean;
    hideLabels?: boolean;
    className?: string;
    disabled?: boolean;
    centerValue?: number;
    useStepChange?: boolean;
    colorVariant?: RangeColorVariant;
    size?: RangeSize;
    showThumbLabel?: boolean;
}

const getLabelVisibility = (values: number[], lowerBound: number, upperBound: number) => {
    const diff = upperBound - lowerBound;
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const factor = 0.15;

    return {
        showLeftLabel: minValue > lowerBound + diff * factor,
        showRightLabel: maxValue < upperBound - diff * factor
    };
};

const sizeClassNameMap: Record<RangeSize, string> = {
    sm: cns.rangeSizeSm,
    md: cns.rangeSizeMd,
    lg: cns.rangeSizeLg
};

interface IRangeColorPalette {
    fill: string;
    trackBg: string;
    thumbBg: string;
    borderColor?: string;
    thresholdColors?: {high: string; medium: string; low: string};
}

const colorPaletteMap: Record<RangeColorVariant, IRangeColorPalette> = {
    [RangeColorVariant.BASE]: {
        fill: getThemeVariable("colors.primary"),
        trackBg: getThemeVariable("colors.background.200"),
        thumbBg: getThemeVariable("colors.primary"),
        borderColor: getThemeVariable("colors.secondary")
    },
    [RangeColorVariant.GREEN]: {
        fill: getThemeVariable("colors.success"),
        trackBg: getThemeVariable("colors.gray.800"),
        thumbBg: getThemeVariable("colors.success"),
        borderColor: getThemeVariable("colors.secondary")
    },
    [RangeColorVariant.RED_YELLOW_GREEN]: {
        fill: getThemeVariable("colors.success"),
        trackBg: getThemeVariable("colors.gray.300"),
        thumbBg: getThemeVariable("colors.success"),
        thresholdColors: {high: "#02D054", medium: "#C6D002", low: "#FF385C"}
    },
    [RangeColorVariant.LAVENDER]: {
        fill: getThemeVariable("colors.highlight"),
        trackBg: getThemeVariable("colors.gray.300"),
        thumbBg: getThemeVariable("colors.highlight")
    }
};

export const Range = (props: IProps) => {
    const {
        name,
        value,
        onChange,
        min,
        max,
        step,
        disabled,
        unitType = UnitsType.NUMBERS,
        useDynamicChange,
        hideLabels,
        className,
        centerValue,
        useStepChange = false,
        colorVariant = RangeColorVariant.BASE,
        size = "sm",
        showThumbLabel
    } = props;
    const isTwoSided = isNumber(value.lower) && isNumber(value.upper);
    const rangeRef = useRef<ReactRange>(null);

    const {sliderValues, sliderStep, minimum, maximum, setSliderValues, mapToRealValue} = useValuesWithTransformation(
        value,
        step,
        min,
        max,
        centerValue,
        useStepChange,
        useDynamicChange
    );

    useEffect(() => {
        if (!useDynamicChange) {
            return;
        }

        const transformedValue = centerValue ? sliderValues.map((el) => mapToRealValue(el)) : sliderValues;

        const currentUpper = value?.upper;
        const newUpper = transformedValue[isTwoSided ? 1 : 0];
        const currentLower = value?.lower;
        const newLower = transformedValue[0];

        const wasUpperClamped = isNumber(currentUpper) && currentUpper > max && newUpper === max;
        const wasLowerClamped = isNumber(currentLower) && currentLower < min && newLower === min;

        if (wasUpperClamped || wasLowerClamped) {
            if (isTwoSided) {
                onChange(name, {
                    lower: newLower,
                    upper: newUpper
                });
            } else {
                onChange(name, {
                    upper: newUpper
                });
            }
        }
    }, [sliderValues, min, max, centerValue, useDynamicChange, isTwoSided]);

    const colorPalette = colorPaletteMap[colorVariant];
    const borderStyle = colorPalette.borderColor ? `1px solid ${colorPalette.borderColor}` : "none";

    const sliderColor = useMemo(() => {
        if (!colorPalette.thresholdColors) {
            return undefined;
        }
        const {high, medium, low} = colorPalette.thresholdColors;
        if (sliderValues[0] >= max * 0.98) {
            return high;
        }
        if (sliderValues[0] >= max * 0.96) {
            return medium;
        }
        return low;
    }, [sliderValues, max, colorPalette]);

    const {showLeftLabel, showRightLabel} = getLabelVisibility(sliderValues, minimum, maximum);

    const renderTrackCallback = useCallback(
        ({props: trackProps, children}: IRenderTrackParams) => {
            return (
                <div
                    onMouseDown={trackProps.onMouseDown}
                    onTouchStart={trackProps.onTouchStart}
                    className={classNames(cns.rangeTrack, size === "lg" && cns.rangeTrackWrapLg)}
                >
                    <div
                        ref={trackProps.ref}
                        className={cns.rangeTrackContent}
                        style={
                            {
                                "--track-bg": getTrackBackground({
                                    values: sliderValues,
                                    colors: isTwoSided
                                        ? [colorPalette.trackBg, colorPalette.fill, colorPalette.trackBg]
                                        : [sliderColor ?? colorPalette.fill, colorPalette.trackBg],
                                    min: minimum,
                                    max: maximum
                                }),
                                "--range-track-border": borderStyle
                            } as CSSProperties
                        }
                    >
                        {children}
                    </div>
                </div>
            );
        },
        [sliderValues, minimum, maximum, isTwoSided, colorPalette, borderStyle, size]
    );

    const isMounted = useIsMounted();
    if (!isMounted) {
        return null;
    }

    return (
        <div className={classNames(className, sizeClassNameMap[size])}>
            <ReactRange
                disabled={disabled}
                ref={rangeRef}
                onFinalChange={(values) => {
                    const newValues = centerValue ? values.map((el) => mapToRealValue(el)) : values;
                    if (isTwoSided) {
                        onChange(name, {
                            lower: newValues[0],
                            upper: newValues[1]
                        });
                        return;
                    }
                    /**
                     * TODO That makes it actually a single value range, like a "MaxRange"
                     *  We should change the name or implementation
                     */
                    onChange(name, {
                        upper: newValues[0]
                    });
                }}
                onChange={(value) => {
                    setSliderValues(value);
                    const transformedValue = centerValue ? value.map((el) => mapToRealValue(el)) : value;

                    if (isTwoSided && useDynamicChange) {
                        onChange(name, {
                            lower: transformedValue[0],
                            upper: transformedValue[1]
                        });
                        return;
                    }
                    if (useDynamicChange) {
                        onChange(name, {upper: transformedValue[0]});
                    }
                }}
                values={sliderValues}
                step={sliderStep}
                min={minimum}
                max={maximum}
                renderTrack={renderTrackCallback}
                renderThumb={({props: {key, ...thumbProps}, value: thumbValue}) => {
                    const displayValue = centerValue ? mapToRealValue(thumbValue) : thumbValue;

                    return (
                        <div {...thumbProps} role="slider" key={key} style={thumbProps.style} className={cns.rangeThumbWrap}>
                            <div className={cns.rangeThumb} style={{backgroundColor: sliderColor ?? colorPalette.thumbBg, border: borderStyle}}></div>
                            {showThumbLabel && (
                                <>
                                    <div className={cns.rangeThumbDecoratorLine}></div>
                                    <div className={cns.rangeThumbDecoratorLabel}>{getLabelFromExtremum(unitType, displayValue)}</div>
                                </>
                            )}
                        </div>
                    );
                }}
            />
            {!hideLabels && (
                <div className={cns.rangeThumbLabel}>
                    <Text variant="info_txt_2" align="left" className={classNames(cns.rangeThumbLabelText, !showLeftLabel && cns.rangeThumbLabelTextHidden)}>
                        {`Od ${getLabelFromExtremum(unitType, min)}`}
                    </Text>
                    <Text variant="info_txt_2" align="right" className={classNames(cns.rangeThumbLabelText, !showRightLabel && cns.rangeThumbLabelTextHidden)}>
                        {`Do ${getLabelFromExtremum(unitType, max)}`}
                    </Text>
                </div>
            )}
        </div>
    );
};
import {CSSProperties, useCallback, useEffect, useMemo, useRef} from "react";
import {getTrackBackground, Range as ReactRange} from "react-range";
import {IRenderTrackParams} from "react-range/lib/types";
import classNames from "classnames";

import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {isNumber} from "@pg-mono/nodash";

import {useValuesWithTransformation} from "../hooks/use_values_with_transformation";
import {IRange} from "../types/IRange";
import {RangeColorVariant} from "../types/RangeColorVariant";
import {RangeSize} from "../types/RangeSize";
import {RangeType} from "../types/RangeType";
import {UnitsType} from "../types/UnitsType";
import {getLabelFromExtremum} from "../utils/get_label_from_extremum";

import * as cns from "./Range.module.css";

interface IProps {
    name: string;
    value: IRange<RangeType>;
    error?: string[];
    onChange: (fieldName: string, value: IRange<RangeType>) => void;
    step: number;
    min: number;
    max: number;
    unitType?: UnitsType;
    useDynamicChange?: boolean;
    hideLabels?: boolean;
    className?: string;
    disabled?: boolean;
    centerValue?: number;
    useStepChange?: boolean;
    colorVariant?: RangeColorVariant;
    size?: RangeSize;
    showThumbLabel?: boolean;
}

const getLabelVisibility = (values: number[], lowerBound: number, upperBound: number) => {
    const diff = upperBound - lowerBound;
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const factor = 0.15;

    return {
        showLeftLabel: minValue > lowerBound + diff * factor,
        showRightLabel: maxValue < upperBound - diff * factor
    };
};

const sizeClassNameMap: Record<RangeSize, string> = {
    sm: cns.rangeSizeSm,
    md: cns.rangeSizeMd,
    lg: cns.rangeSizeLg
};

interface IRangeColorPalette {
    fill: string;
    trackBg: string;
    thumbBg: string;
    borderColor?: string;
    thresholdColors?: {high: string; medium: string; low: string};
}

const colorPaletteMap: Record<RangeColorVariant, IRangeColorPalette> = {
    [RangeColorVariant.BASE]: {
        fill: getThemeVariable("colors.primary"),
        trackBg: getThemeVariable("colors.background.200"),
        thumbBg: getThemeVariable("colors.primary"),
        borderColor: getThemeVariable("colors.secondary")
    },
    [RangeColorVariant.GREEN]: {
        fill: getThemeVariable("colors.success"),
        trackBg: getThemeVariable("colors.gray.800"),
        thumbBg: getThemeVariable("colors.success"),
        borderColor: getThemeVariable("colors.secondary")
    },
    [RangeColorVariant.RED_YELLOW_GREEN]: {
        fill: getThemeVariable("colors.success"),
        trackBg: getThemeVariable("colors.gray.300"),
        thumbBg: getThemeVariable("colors.success"),
        thresholdColors: {high: "#02D054", medium: "#C6D002", low: "#FF385C"}
    },
    [RangeColorVariant.LAVENDER]: {
        fill: getThemeVariable("colors.highlight"),
        trackBg: getThemeVariable("colors.gray.300"),
        thumbBg: getThemeVariable("colors.highlight")
    }
};

export const Range = (props: IProps) => {
    const {
        name,
        value,
        onChange,
        min,
        max,
        step,
        disabled,
        unitType = UnitsType.NUMBERS,
        useDynamicChange,
        hideLabels,
        className,
        centerValue,
        useStepChange = false,
        colorVariant = RangeColorVariant.BASE,
        size = "sm",
        showThumbLabel
    } = props;
    const isTwoSided = isNumber(value.lower) && isNumber(value.upper);
    const rangeRef = useRef<ReactRange>(null);

    const {sliderValues, sliderStep, minimum, maximum, setSliderValues, mapToRealValue} = useValuesWithTransformation(
        value,
        step,
        min,
        max,
        centerValue,
        useStepChange,
        useDynamicChange
    );

    useEffect(() => {
        if (!useDynamicChange) {
            return;
        }

        const transformedValue = centerValue ? sliderValues.map((el) => mapToRealValue(el)) : sliderValues;

        const currentUpper = value?.upper;
        const newUpper = transformedValue[isTwoSided ? 1 : 0];
        const currentLower = value?.lower;
        const newLower = transformedValue[0];

        const wasUpperClamped = isNumber(currentUpper) && currentUpper > max && newUpper === max;
        const wasLowerClamped = isNumber(currentLower) && currentLower < min && newLower === min;

        if (wasUpperClamped || wasLowerClamped) {
            if (isTwoSided) {
                onChange(name, {
                    lower: newLower,
                    upper: newUpper
                });
            } else {
                onChange(name, {
                    upper: newUpper
                });
            }
        }
    }, [sliderValues, min, max, centerValue, useDynamicChange, isTwoSided]);

    const colorPalette = colorPaletteMap[colorVariant];
    const borderStyle = colorPalette.borderColor ? `1px solid ${colorPalette.borderColor}` : "none";

    const sliderColor = useMemo(() => {
        if (!colorPalette.thresholdColors) {
            return undefined;
        }
        const {high, medium, low} = colorPalette.thresholdColors;
        if (sliderValues[0] >= max * 0.98) {
            return high;
        }
        if (sliderValues[0] >= max * 0.96) {
            return medium;
        }
        return low;
    }, [sliderValues, max, colorPalette]);

    const {showLeftLabel, showRightLabel} = getLabelVisibility(sliderValues, minimum, maximum);

    const renderTrackCallback = useCallback(
        ({props: trackProps, children}: IRenderTrackParams) => {
            return (
                <div
                    onMouseDown={trackProps.onMouseDown}
                    onTouchStart={trackProps.onTouchStart}
                    className={classNames(cns.rangeTrack, size === "lg" && cns.rangeTrackWrapLg)}
                >
                    <div
                        ref={trackProps.ref}
                        className={cns.rangeTrackContent}
                        style={
                            {
                                "--track-bg": getTrackBackground({
                                    values: sliderValues,
                                    colors: isTwoSided
                                        ? [colorPalette.trackBg, colorPalette.fill, colorPalette.trackBg]
                                        : [sliderColor ?? colorPalette.fill, colorPalette.trackBg],
                                    min: minimum,
                                    max: maximum
                                }),
                                "--range-track-border": borderStyle
                            } as CSSProperties
                        }
                    >
                        {children}
                    </div>
                </div>
            );
        },
        [sliderValues, minimum, maximum, isTwoSided, colorPalette, borderStyle, size]
    );

    const isMounted = useIsMounted();
    if (!isMounted) {
        return null;
    }

    return (
        <div className={classNames(className, sizeClassNameMap[size])}>
            <ReactRange
                disabled={disabled}
                ref={rangeRef}
                onFinalChange={(values) => {
                    const newValues = centerValue ? values.map((el) => mapToRealValue(el)) : values;
                    if (isTwoSided) {
                        onChange(name, {
                            lower: newValues[0],
                            upper: newValues[1]
                        });
                        return;
                    }
                    /**
                     * TODO That makes it actually a single value range, like a "MaxRange"
                     *  We should change the name or implementation
                     */
                    onChange(name, {
                        upper: newValues[0]
                    });
                }}
                onChange={(value) => {
                    setSliderValues(value);
                    const transformedValue = centerValue ? value.map((el) => mapToRealValue(el)) : value;

                    if (isTwoSided && useDynamicChange) {
                        onChange(name, {
                            lower: transformedValue[0],
                            upper: transformedValue[1]
                        });
                        return;
                    }
                    if (useDynamicChange) {
                        onChange(name, {upper: transformedValue[0]});
                    }
                }}
                values={sliderValues}
                step={sliderStep}
                min={minimum}
                max={maximum}
                renderTrack={renderTrackCallback}
                renderThumb={({props: {key, ...thumbProps}, value: thumbValue}) => {
                    const displayValue = centerValue ? mapToRealValue(thumbValue) : thumbValue;

                    return (
                        <div {...thumbProps} role="slider" key={key} style={thumbProps.style} className={cns.rangeThumbWrap}>
                            <div className={cns.rangeThumb} style={{backgroundColor: sliderColor ?? colorPalette.thumbBg, border: borderStyle}}></div>
                            {showThumbLabel && (
                                <>
                                    <div className={cns.rangeThumbDecoratorLine}></div>
                                    <div className={cns.rangeThumbDecoratorLabel}>{getLabelFromExtremum(unitType, displayValue)}</div>
                                </>
                            )}
                        </div>
                    );
                }}
            />
            {!hideLabels && (
                <div className={cns.rangeThumbLabel}>
                    <Text variant="info_txt_2" align="left" className={classNames(cns.rangeThumbLabelText, !showLeftLabel && cns.rangeThumbLabelTextHidden)}>
                        {`Od ${getLabelFromExtremum(unitType, min)}`}
                    </Text>
                    <Text variant="info_txt_2" align="right" className={classNames(cns.rangeThumbLabelText, !showRightLabel && cns.rangeThumbLabelTextHidden)}>
                        {`Do ${getLabelFromExtremum(unitType, max)}`}
                    </Text>
                </div>
            )}
        </div>
    );
};
